import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/joy";
import { useEquipment } from "../../contexts/api";
import { useAlert } from "../../contexts/app/notificationToast";
import useAppSettings from "../../contexts/app/appSettings";
import type {
  EquipmentModel,
  Equipment,
  FilterCriteria,
  EquipmentFilters,
  EquipmentSearchRequest,
  EquipmentPrefilter,
} from "../../api/equipment/Equipment.typed";
import { extractDataFromResponse } from "../../utils/paginationHelpers";
import EquipmentsToolbar from "./display/EquipmentsToolbar";
import EquipmentsFilterDrawer from "./display/EquipmentsFilterDrawer";
import EquipmentCards from "./display/EquipmentCards";
import Pagination from "../common/Pagination";
import EquipmentExportModal from "./display/EquipmentExportModal";
import QRCodeRegenerateModal from "./display/QRCodeRegenerateModal";
import { usePermissions, Permission } from "../../contexts/app/permissions";
import { colorTokens } from "../../styles/globalStyles";
import { useEquipmentSort } from "./hooks/useEquipmentSort";
import { useEquipmentSelection } from "./hooks/useEquipmentSelection";
import { useEquipmentEditing } from "./hooks/useEquipmentEditing";
import { useEquipmentExports } from "./hooks/useEquipmentExports";

const EQUIPMENTS_PAGE_SIZE = 20;

function sameModelId(
  left: number | string | undefined | null,
  right: number | string | undefined | null,
): boolean {
  if (left == null || right == null || left === "" || right === "") return false;

  return Number(left) === Number(right);
}

function findModelForAlert(
  models: EquipmentModel[],
  modelId?: number,
  modelName?: string,
): EquipmentModel | undefined {
  if (modelId != null) {
    const found = models.find((mm) => sameModelId(mm.ID, modelId));
    if (found) return found;
  }

  if (!modelName) return undefined;

  return models.find((mm) => mm.Name === modelName);
}

type InventoryRouteState = {
  select?: string;
  modelId?: number;
  modelName?: string;
  filters?: EquipmentFilters;
  alertName?: string;
};

export default function EquipmentsComponent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.EQUIPMENT_EDIT);
  const {
    getEquipmentModels,
    getEquipmentsByModelID,
    searchEquipmentsByModelID,
    createEquipment,
    updateEquipment,
    deleteEquipment,
    regenerateEquipmentQRCode,
  } = useEquipment();
  const { showAlert } = useAlert();
  const { config } = useAppSettings();

  const [models, setModels] = React.useState<EquipmentModel[]>([]);
  const [selectedModel, setSelectedModel] = React.useState<EquipmentModel | undefined>();
  const [equipments, setEquipments] = React.useState<Equipment[]>([]);
  // Criteria coming from an alert: applied transparently, not shown as chips.
  const [alertCriteria, setAlertCriteria] = React.useState<FilterCriteria[]>([]);
  // Criteria added manually by the user in the drawer.
  const [searchCriteria, setSearchCriteria] = React.useState<FilterCriteria[]>([]);
  const [searchLogic, setSearchLogic] = React.useState<"AND" | "OR">("AND");
  const [loading, setLoading] = React.useState(false);
  const [qrMap, setQrMap] = React.useState<Record<string, string>>({});
  const [activePrefilter, setActivePrefilter] = React.useState<EquipmentPrefilter | null>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = React.useState(false);
  const [alertSourceName, setAlertSourceName] = React.useState<string | undefined>(undefined);

  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(0);
  const [totalItems, setTotalItems] = React.useState(0);
  const [itemsPerPage, setItemsPerPage] = React.useState(EQUIPMENTS_PAGE_SIZE);

  const sort = useEquipmentSort({
    selectedModel,
    onSortChange: (sortByValue, sortOrderValue) => {
      if (!selectedModel) return;
      setCurrentPage(1);
      loadEquipments(
        selectedModel.ID!,
        1,
        itemsPerPage,
        [...alertCriteria, ...searchCriteria],
        activePrefilter,
        sortByValue,
        sortOrderValue,
      );
    },
  });

  const selection = useEquipmentSelection(equipments);

  const loadModels = React.useCallback(async () => {
    const m = await getEquipmentModels();
    if (m) {
      const extracted = extractDataFromResponse<EquipmentModel>(m);
      setModels(extracted);
    }
  }, [getEquipmentModels]);

  // Drop list responses that belong to a previous model or filter request.
  const loadSeqRef = React.useRef(0);
  const clearSelection = selection.clearSelection;

  const loadEquipments = React.useCallback(
    async (
      modelId: number,
      page = 1,
      limit = itemsPerPage,
      criteria = [...alertCriteria, ...searchCriteria],
      prefilter: EquipmentPrefilter | null = activePrefilter,
      sortByParam: string | undefined = sort.sortBy,
      sortOrderParam: "ASC" | "DESC" | undefined = sort.sortOrder,
    ) => {
      const seq = loadSeqRef.current + 1;
      loadSeqRef.current = seq;
      setLoading(true);
      setEquipments([]);
      setQrMap({});

      let result;

      const hasSort = !!sortByParam || !!sortOrderParam;
      if (prefilter || criteria.length > 0 || hasSort) {
        const searchRequest: EquipmentSearchRequest = {
          ModelID: modelId,
          Page: page,
          Limit: limit,
          PrefilterID: prefilter?.ID,
          Filters:
            criteria.length > 0
              ? {
                  Criteria: criteria,
                  Logic: searchLogic,
                }
              : undefined,
          ...(hasSort ? { SortBy: sortByParam || "", SortOrder: sortOrderParam || "ASC" } : {}),
        };
        result = await searchEquipmentsByModelID(searchRequest);
      } else {
        result = await getEquipmentsByModelID(modelId, page, limit);
      }

      if (seq !== loadSeqRef.current) return;

      if (result) {
        const matching = result.equipments.filter(
          (eq) => eq.ModelID == null || sameModelId(eq.ModelID, modelId),
        );
        setEquipments(matching);
        setTotalItems(result.total);
        setTotalPages(result.pages);
        setCurrentPage(page);
        clearSelection();
        const qrObj: Record<string, string> = {};
        matching.forEach((eq) => {
          if (!eq.ID) return;
          const qrCode = eq.QRCode;
          if (!qrCode) return;
          qrObj[eq.ID] = typeof qrCode === "string" ? qrCode : qrCode.QRCode;
        });
        setQrMap(qrObj);
      } else {
        setEquipments([]);
        setTotalItems(0);
        setTotalPages(0);
      }
      setLoading(false);
    },
    [
      getEquipmentsByModelID,
      searchEquipmentsByModelID,
      itemsPerPage,
      alertCriteria,
      searchCriteria,
      activePrefilter,
      searchLogic,
      sort.sortBy,
      sort.sortOrder,
      clearSelection,
    ],
  );

  const reloadCurrentPage = React.useCallback(() => {
    if (selectedModel) loadEquipments(selectedModel.ID!, currentPage);
  }, [selectedModel, currentPage, loadEquipments]);

  const editing = useEquipmentEditing({
    canManage,
    selectedModel,
    setEquipments,
    showAlert,
    createEquipment,
    updateEquipment,
    deleteEquipment,
    reloadCurrentPage,
    onAfterDelete: selection.removeFromSelection,
  });

  const exports = useEquipmentExports({
    selectedModel,
    equipments,
    qrMap,
    selectedIds: selection.selectedIds,
    showAlert,
    logoQrCodeUrl: config?.LogoQrCodeUrl,
    searchCriteria: [...alertCriteria, ...searchCriteria],
    searchLogic,
    activePrefilter,
    sortBy: sort.sortBy || undefined,
    sortOrder: sort.effectiveSortOrder,
    regenerateEquipmentQRCode,
    searchEquipmentsByModelID,
    reloadCurrentPage,
  });

  React.useEffect(() => {
    loadModels();
  }, [loadModels]);

  // Latest loadEquipments kept in a ref so the navigation-consumption effect below
  // does not re-fire every time loadEquipments is rebuilt (which happens when we
  // mutate alertCriteria/searchCriteria inside the effect itself → infinite loop).
  const loadEquipmentsRef = React.useRef(loadEquipments);
  React.useEffect(() => {
    loadEquipmentsRef.current = loadEquipments;
  }, [loadEquipments]);

  React.useEffect(() => {
    const state = location.state as InventoryRouteState | null | undefined;
    if (!state) return;
    const mid = state.modelId;
    const filters = state.filters;
    const aName = state.alertName;
    // Nothing actionable in this state payload: skip without re-navigating.
    if (!aName && mid == null) return;
    if (!models || models.length === 0) return;
    const m = findModelForAlert(models, mid, state.modelName);
    if (!m?.ID) return;
    if (aName) setAlertSourceName(aName);
    editing.cancelEdit();
    setActivePrefilter(null);
    setSelectedModel(m);
    sort.resetSort();
    if (filters && filters.Criteria) {
      // From an alert: keep criteria implicit (alert chip only). Otherwise treat as manual.
      if (aName) {
        setAlertCriteria(filters.Criteria);
        setSearchCriteria([]);
      } else {
        setAlertCriteria([]);
        setSearchCriteria(filters.Criteria);
      }
      setSearchLogic(filters.Logic || "AND");
      setCurrentPage(1);
      loadEquipmentsRef.current(
        m.ID,
        1,
        itemsPerPage,
        filters.Criteria,
        null,
        undefined,
        undefined,
      );
    } else {
      setAlertCriteria([]);
      setCurrentPage(1);
      loadEquipmentsRef.current(m.ID, 1, itemsPerPage, [], null, undefined, undefined);
    }
    // Consume alert state so clearing the filter (cross) is not overwritten when effect re-runs
    navigate(location.pathname, {
      replace: true,
      state: { select: state.select || "Équipements" },
    });
    // We intentionally omit `loadEquipments`, `sort` (full object) and other reactive
    // helpers from the deps: they would re-run this effect when state we set inside it
    // changes, causing an infinite update loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsPerPage, location.state, location.pathname, models, navigate, sort.resetSort]);

  React.useEffect(() => {
    const handleFocus = () => loadModels();
    window.addEventListener("focus", handleFocus);

    return () => window.removeEventListener("focus", handleFocus);
  }, [loadModels]);

  React.useEffect(() => {
    if (!selectedModel || models.length === 0) return;

    const updatedModel = models.find((model) => sameModelId(model.ID, selectedModel.ID));
    if (!updatedModel) return;

    if (activePrefilter) {
      const prefilterStillExists = updatedModel.Prefilters?.some(
        (p: EquipmentPrefilter) => p.ID === activePrefilter.ID,
      );
      if (!prefilterStillExists) {
        setActivePrefilter(null);
        loadEquipments(
          selectedModel.ID!,
          1,
          itemsPerPage,
          [...alertCriteria, ...searchCriteria],
          null,
        );
      }
    }

    if (updatedModel !== selectedModel) {
      setSelectedModel(updatedModel);
    }
  }, [
    models,
    selectedModel,
    activePrefilter,
    loadEquipments,
    itemsPerPage,
    alertCriteria,
    searchCriteria,
  ]);

  // Reset to page 1 and reload using the current sort, with explicit criteria/prefilter.
  const reloadFromTop = React.useCallback(
    (modelId: number, criteria: FilterCriteria[], prefilter: EquipmentPrefilter | null) => {
      setCurrentPage(1);
      loadEquipments(modelId, 1, itemsPerPage, criteria, prefilter, sort.sortBy, sort.sortOrder);
    },
    [itemsPerPage, loadEquipments, sort.sortBy, sort.sortOrder],
  );

  const handleSelectModel = React.useCallback(
    (_: React.SyntheticEvent | null, value: number | null) => {
      if (value === -1 || value == null) return;
      if (sameModelId(selectedModel?.ID, value)) return;
      const m = models.find((mm) => sameModelId(mm.ID, value));
      editing.cancelEdit();
      setSelectedModel(m);
      setAlertCriteria([]);
      setAlertSourceName(undefined);
      setSearchCriteria([]);
      setSearchLogic("AND");
      setActivePrefilter(null);
      sort.resetSort();
      if (m && m.ID !== undefined) {
        setCurrentPage(1);
        loadEquipments(m.ID, 1, itemsPerPage, [], null, undefined, undefined);
      }
      clearSelection();
    },
    [
      models,
      itemsPerPage,
      loadEquipments,
      sort.resetSort,
      selectedModel?.ID,
      editing.cancelEdit,
      clearSelection,
    ],
  );

  const handleAddCriteria = React.useCallback(
    (criteria: FilterCriteria) => {
      const newManual = [...searchCriteria, criteria];
      setSearchCriteria(newManual);
      if (selectedModel) {
        reloadFromTop(selectedModel.ID!, [...alertCriteria, ...newManual], activePrefilter);
      }
    },
    [searchCriteria, alertCriteria, selectedModel, activePrefilter, reloadFromTop],
  );

  const handleRemoveCriteria = React.useCallback(
    (criteriaIndex: number) => {
      const newManual = searchCriteria.filter((_, index) => index !== criteriaIndex);
      setSearchCriteria(newManual);
      if (!selectedModel) return;
      reloadFromTop(selectedModel.ID!, [...alertCriteria, ...newManual], activePrefilter);
    },
    [searchCriteria, alertCriteria, selectedModel, activePrefilter, reloadFromTop],
  );

  const handleApplyPrefilter = React.useCallback(
    (prefilter: EquipmentPrefilter | null) => {
      setActivePrefilter(prefilter);
      setAlertCriteria([]);
      setAlertSourceName(undefined);
      setSearchCriteria([]);
      setSearchLogic("AND");
      if (selectedModel) reloadFromTop(selectedModel.ID!, [], prefilter);
    },
    [selectedModel, reloadFromTop],
  );

  // Remove the alert chip only: clear alert criteria but keep any manual criteria.
  const handleClearAlertOnly = React.useCallback(() => {
    setAlertCriteria([]);
    setAlertSourceName(undefined);
    if (selectedModel) {
      reloadFromTop(selectedModel.ID!, searchCriteria, activePrefilter);
    }
  }, [selectedModel, searchCriteria, activePrefilter, reloadFromTop]);

  // Drawer "reset" button: wipe alert, manual criteria and prefilter together.
  const handleResetAllFilters = React.useCallback(() => {
    setAlertCriteria([]);
    setAlertSourceName(undefined);
    setSearchCriteria([]);
    setSearchLogic("AND");
    setActivePrefilter(null);
    if (selectedModel) reloadFromTop(selectedModel.ID!, [], null);
  }, [selectedModel, reloadFromTop]);

  const handleToggleFilterDrawer = React.useCallback(
    () => setFilterDrawerOpen((prev) => !prev),
    [],
  );
  const handleCloseFilterDrawer = React.useCallback(() => setFilterDrawerOpen(false), []);

  const handlePageChange = React.useCallback(
    (newPage: number) => {
      if (selectedModel && newPage >= 1 && newPage <= totalPages) {
        loadEquipments(selectedModel.ID!, newPage);
      }
    },
    [selectedModel, totalPages, loadEquipments],
  );

  const handleItemsPerPageChange = React.useCallback(
    (newLimit: number) => {
      setItemsPerPage(newLimit);
      if (selectedModel) {
        setCurrentPage(1);
        loadEquipments(selectedModel.ID!, 1, newLimit);
      }
    },
    [selectedModel, loadEquipments],
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <EquipmentsToolbar
        models={models}
        selectedModelId={selectedModel?.ID ?? null}
        selectedModel={selectedModel}
        canManage={canManage}
        onSelectModel={handleSelectModel}
        onCreate={editing.openCreate}
        activePrefilter={activePrefilter}
        onApplyPrefilter={handleApplyPrefilter}
        activeCriteriaCount={searchCriteria.length + (alertSourceName ? 1 : 0)}
        filterDrawerOpen={filterDrawerOpen}
        onToggleFilterDrawer={handleToggleFilterDrawer}
        sortBy={sort.sortBy}
        effectiveSortOrder={sort.effectiveSortOrder}
        sortableAttributes={sort.sortableAttributes}
        currentSortAttr={sort.currentSortAttr}
        onPickSort={sort.pickSort}
        onToggleSortOrder={sort.toggleSortOrder}
        onExportFiltered={exports.exportFilteredList}
        exporting={exports.exporting}
      />

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexGrow: 1,
            minHeight: 0,
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        selectedModel && (
          <Box
            sx={{
              flexGrow: 1,
              minHeight: 0,
              position: "relative",
              display: "flex",
              flexDirection: "row",
              borderTop: `1px solid ${colorTokens.border.default}`,
            }}
          >
            <EquipmentsFilterDrawer
              open={filterDrawerOpen}
              onClose={handleCloseFilterDrawer}
              selectedModel={selectedModel}
              searchCriteria={searchCriteria}
              searchLogic={searchLogic}
              onAddCriteria={handleAddCriteria}
              onRemoveCriteria={handleRemoveCriteria}
              onResetAll={handleResetAllFilters}
              onSearchLogicChange={setSearchLogic}
              alertName={alertSourceName}
              onClearAlertName={handleClearAlertOnly}
            />

            <Box
              sx={{
                flexGrow: 1,
                minWidth: 0,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                <EquipmentCards
                  selectedModel={selectedModel}
                  equipments={equipments}
                  selectedIds={selection.selectedIds}
                  editingIndex={editing.editingIndex}
                  editItem={editing.editItem}
                  qrMap={qrMap}
                  hasActiveFilters={searchCriteria.length > 0}
                  onToggleSelect={selection.toggleSelect}
                  onToggleSelectAll={selection.toggleSelectAll}
                  onEditEquipment={editing.openEditEquip}
                  onDeleteEquipment={editing.handleDelete}
                  onSaveEquipment={editing.saveEquip}
                  saveEquipmentPending={editing.equipmentSavePending}
                  onCancelEdit={editing.cancelEdit}
                  onEditItemChange={editing.setEditItem}
                  onOpenExport={exports.openExportForEquipment}
                  onOpenRegen={exports.openRegenForEquipment}
                  onOpenSelectionExport={exports.openSelectionExport}
                  onOpenSelectionRegen={exports.openSelectionRegen}
                />
              </Box>

              <Box
                sx={{
                  borderTop: `1px solid ${colorTokens.border.default}`,
                  background: `linear-gradient(90deg, ${colorTokens.bg.surfaceMuted} 0%, ${colorTokens.bg.surface} 100%)`,
                  px: 2,
                  py: 1,
                  flexShrink: 0,
                }}
              >
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={totalItems}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                />
              </Box>
            </Box>
          </Box>
        )
      )}

      <EquipmentExportModal
        open={exports.exportOpen}
        selectedModel={selectedModel}
        exportAttrIds={exports.exportAttrIds}
        onClose={exports.closeExportModal}
        onAttributeToggle={exports.toggleExportAttr}
        onExport={exports.exportQRCodes}
      />

      <QRCodeRegenerateModal
        open={exports.regenOpen}
        onClose={exports.closeRegenModal}
        onConfirm={exports.regenerateSelectedQRCodes}
      />
    </Box>
  );
}
